import React, { useCallback, useState, useRef, useEffect, useMemo } from 'react';
import { Alert, Pressable, Text, View, Platform, ScrollView, TextInput, useWindowDimensions, Switch } from 'react-native';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { MotiView } from 'moti';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { XStack, YStack } from 'tamagui';
import { StageItem } from './EnhancedFlowHierarchy';
import UserDataSelector from './UserDataSelector';
import CalendarSelector from './CalendarSelector';
import apis from '@api/api';

interface StageEditorProps {
  isVisible: boolean;
  stage: StageItem | null;
  onClose: () => void;
  onSave: (stageId: string, updatedStage: Partial<StageItem>) => void;
  flowId?: string;
}

const StageEditor: React.FC<StageEditorProps> = ({ isVisible, stage, onClose, onSave, flowId }) => {
  const theme = themeSelector();
  const { height, width } = useWindowDimensions();

  // Safety check: Don't render if stage is invalid
  if (!isVisible || !stage || !stage._id) {
    return null;
  }

  // Editor state
  const [editedStage, setEditedStage] = useState<StageItem | null>(stage);
  const [activeTab, setActiveTab] = useState<'basic' | 'config' | 'voice' | 'data' | 'actions' | 'conditions' | 'verification'>('basic');

  // Modal states for selectors
  const [showUserDataSelector, setShowUserDataSelector] = useState(false);
  const [showCalendarSelector, setShowCalendarSelector] = useState(false);
  const [showVerificationDataSelector, setShowVerificationDataSelector] = useState(false);

  // Condition editor state
  const [editingConditionIndex, setEditingConditionIndex] = useState<number | null>(null);
  const [showConditionEditor, setShowConditionEditor] = useState(false);

  // Theme-based colors for editor
  const editorColors = useMemo(() => ({
    background: theme ? '#ffffff' : '#1a1a1a',
    surface: theme ? '#f8f9fa' : '#2d2d2d',
    border: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
    text: theme ? '#333333' : '#e0e0e0',
    accent: theme ? '#7c3aed' : '#a78bfa',
    secondaryText: theme ? '#64748b' : '#94a3b8',
    cardBg: theme ? '#f1f5f9' : '#334155',
  }), [theme]);

  // Initialize edited stage when stage prop changes
  useEffect(() => {
    if (stage) {
      setEditedStage({ ...stage });
      setActiveTab('basic');
    }
  }, [stage]);

  // Get stage type icon and color
  const getStageTypeInfo = useCallback((type: string) => {
    const types = {
      greeting: { icon: 'hand-wave', color: '#10b981' },
      introduction: { icon: 'information', color: '#3b82f6' },
      data_collection: { icon: 'form-select', color: '#f59e0b' },
      setup: { icon: 'cog', color: '#6366f1' },
      training: { icon: 'school', color: '#8b5cf6' },
      completion: { icon: 'check-circle', color: '#059669' },
      diagnosis: { icon: 'stethoscope', color: '#ef4444' },
      testing: { icon: 'test-tube', color: '#ec4899' },
      resolution: { icon: 'check-decagram', color: '#10b981' },
      action: { icon: 'lightning-bolt', color: '#f59e0b' },
      decision: { icon: 'call-split', color: '#8b5cf6' },
      notification: { icon: 'bell', color: '#06b6d4' },
      approval: { icon: 'check-circle-outline', color: '#059669' },
      review: { icon: 'clipboard-check', color: '#3b82f6' },
      verification: { icon: 'shield-check', color: '#0891b2' },
      custom: { icon: 'puzzle', color: '#64748b' },
    };
    return types[type] || types.custom;
  }, []);

  // Handle save
  const handleSave = useCallback(() => {
    if (!editedStage) {
      console.error('StageEditor: editedStage is null');
      return;
    }

    if (!editedStage._id) {
      console.error('StageEditor: editedStage._id is missing', editedStage);
      Alert.alert('Error', 'Stage ID is missing. Cannot save changes.');
      return;
    }

    try {
      onSave(editedStage._id, editedStage);
      onClose();
    } catch (error) {
      console.error('Error saving stage:', error);
      Alert.alert('Error', 'Failed to save stage changes');
    }
  }, [editedStage, onSave, onClose]);

  // Update field helper
  const updateField = useCallback((field: string, value: any) => {
    setEditedStage(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  // Update nested field helper
  const updateNestedField = useCallback((parent: string, field: string, value: any) => {
    setEditedStage(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [parent]: {
          ...(prev[parent] || {}),
          [field]: value
        }
      };
    });
  }, []);

  // Handle user data selection and fetch schema
  const handleUserDataSelect = useCallback(async (userDataId: string) => {
    console.log('Selected user_datas ID:', userDataId);

    try {
      // Fetch the full schema from backend using the pattern from DataBrowserContext
      const response = await apis.getDataSchema(userDataId, userDataId);
      const schemaData = response?.data?.data;

      if (schemaData && schemaData.dataSchema) {
        console.log('✅ Fetched schema:', schemaData.dataTitle);
        console.log('📋 Schema fields:', schemaData.dataSchema);

        // Extract fields from schema (handle different schema formats)
        let extractedFields = [];

        if (Array.isArray(schemaData.dataSchema)) {
          // Direct array of field objects
          extractedFields = schemaData.dataSchema.map(field => ({
            name: field.title || field.name || '',
            type: field.type || 'text',
            required: field.validation?.required || field.required || false,
            validation: field.validation,
            helpText: field.display?.helpText || field.content?.helpText || field.helpText,
            example: field.display?.example || field.content?.example || field.example,
            placeholder: field.display?.placeholder || field.content?.placeholder || field.placeholder,
            label: field.display?.label || field.content?.label || field.label || field.title || field.name,
            options: field.validation?.enum || field.content?.enum || field.options
          }));
        } else if (schemaData.dataSchema.fields) {
          extractedFields = schemaData.dataSchema.fields.map(field => ({
            name: field.title || field.name || '',
            type: field.type || 'text',
            required: field.validation?.required || field.required || false,
            validation: field.validation,
            helpText: field.display?.helpText || field.content?.helpText || field.helpText,
            example: field.display?.example || field.content?.example || field.example,
            placeholder: field.display?.placeholder || field.content?.placeholder || field.placeholder,
            label: field.display?.label || field.content?.label || field.label || field.title || field.name,
            options: field.validation?.enum || field.content?.enum || field.options
          }));
        } else if (schemaData.dataSchema.properties) {
          // JSON Schema format
          extractedFields = Object.entries(schemaData.dataSchema.properties).map(([name, def]: [string, any]) => ({
            name,
            type: def.type,
            required: schemaData.dataSchema.required?.includes(name),
            validation: def.pattern || def.format || def.validation,
            helpText: def.description || def.content?.helpText,
            example: def.example || def.content?.example,
            placeholder: def.placeholder || def.content?.placeholder,
            label: def.title || def.content?.label || name,
            options: def.enum || def.content?.enum || def.validation?.enum
          }));
        }

        // Ensure all extracted fields have safe values (no objects where strings expected)
        extractedFields = extractedFields.map(field => ({
          ...field,
          // Ensure name and label are strings
          name: String(field.name || field.title || ''),
          label: String(field.label || field.name || field.title || ''),
          type: String(field.type || 'text'),
          // Keep validation as-is (can be string or object, we handle both in display)
          // Ensure arrays are arrays
          options: Array.isArray(field.options) ? field.options : (field.options ? [field.options] : undefined)
        }));

        console.log(`📊 Extracted ${extractedFields.length} fields from schema`);

        // Update stage with schema reference AND populated fields
        setEditedStage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            dataCollection: {
              ...(prev.dataCollection || {}),
              form: userDataId,
              fields: extractedFields,
              schemaFields: extractedFields
            }
          };
        });

        Alert.alert(
          'Schema Linked',
          `Successfully linked to "${schemaData.dataTitle}" with ${extractedFields.length} fields. Fields have been auto-populated.`
        );
      } else {
        console.warn('⚠️ Schema data not found in response');
        // Still link the form reference
        setEditedStage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            dataCollection: {
              ...(prev.dataCollection || {}),
              form: userDataId
            }
          };
        });

        Alert.alert('Schema Linked', 'Schema linked but fields could not be auto-populated. Configure manually.');
      }
    } catch (error) {
      console.error('❌ Error fetching schema:', error);
      Alert.alert('Error', 'Failed to fetch schema details. The link was saved but fields were not populated.');

      // Still save the reference
      setEditedStage(prev => {
        if (!prev) return null;
        return {
          ...prev,
          dataCollection: {
            ...(prev.dataCollection || {}),
            form: userDataId
          }
        };
      });
    }
  }, []);

  // Handle calendar selection
  const handleCalendarSelect = useCallback((calendarId: string) => {
    console.log('Selected calendar ID:', calendarId);
    // Update the most recent schedule_appointment action's calendarConfig
    setEditedStage(prev => {
      if (!prev) return null;
      const updatedActions = [...(prev.stageActions || [])];
      const scheduleActionIndex = updatedActions.findIndex(a => a.actionType === 'schedule_appointment');

      if (scheduleActionIndex >= 0) {
        updatedActions[scheduleActionIndex] = {
          ...updatedActions[scheduleActionIndex],
          calendarConfig: {
            ...(updatedActions[scheduleActionIndex].calendarConfig || {}),
            calendarId: calendarId
          }
        };
      }

      return {
        ...prev,
        stageActions: updatedActions
      };
    });
  }, []);

  // Handle verification data selection
  const handleVerificationDataSelect = useCallback(async (dataSchemaId: string) => {
    console.log('Selected verification schema ID:', dataSchemaId);

    try {
      const response = await apis.getDataSchema(dataSchemaId, dataSchemaId);
      const schemaData = response?.data?.data;

      if (schemaData && schemaData.dataSchema) {
        let extractedFields = [];

        if (Array.isArray(schemaData.dataSchema)) {
          extractedFields = schemaData.dataSchema.map(field => ({
            name: field.title || field.name || '',
            type: field.type || 'text',
            label: field.display?.label || field.label || field.title || field.name
          }));
        } else if (schemaData.dataSchema.fields) {
          extractedFields = schemaData.dataSchema.fields.map(field => ({
            name: field.title || field.name || '',
            type: field.type || 'text',
            label: field.display?.label || field.label || field.title || field.name
          }));
        } else if (schemaData.dataSchema.properties) {
          extractedFields = Object.entries(schemaData.dataSchema.properties).map(([name, def]: [string, any]) => ({
            name,
            type: def.type,
            label: def.title || def.content?.label || name
          }));
        }

        setEditedStage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            verificationConfig: {
              ...(prev.verificationConfig || {}),
              dataSchemaId: dataSchemaId,
              availableFields: extractedFields,
              fieldsToVerify: prev.verificationConfig?.fieldsToVerify || [],
              matchThreshold: prev.verificationConfig?.matchThreshold || 100
            }
          };
        });

        Alert.alert(
          'Verification Schema Linked',
          `Successfully linked to "${schemaData.dataTitle}" with ${extractedFields.length} available fields.`
        );
      } else {
        setEditedStage(prev => {
          if (!prev) return null;
          return {
            ...prev,
            verificationConfig: {
              ...(prev.verificationConfig || {}),
              dataSchemaId: dataSchemaId
            }
          };
        });
        Alert.alert('Schema Linked', 'Schema linked but fields could not be auto-populated.');
      }
    } catch (error) {
      console.error('Error fetching verification schema:', error);
      Alert.alert('Error', 'Failed to fetch schema details.');

      setEditedStage(prev => {
        if (!prev) return null;
        return {
          ...prev,
          verificationConfig: {
            ...(prev.verificationConfig || {}),
            dataSchemaId: dataSchemaId
          }
        };
      });
    }
  }, []);

  // Update condition rule
  const updateConditionRule = useCallback((index: number, field: string, value: any) => {
    setEditedStage(prev => {
      if (!prev) return null;
      const currentRules = [...(prev.conditions?.nextStageRules || [])];

      if (index >= currentRules.length) return prev;

      const updatedRule = { ...currentRules[index] };

      // Update nested condition object
      if (field.startsWith('condition.')) {
        const conditionField = field.replace('condition.', '');
        updatedRule.condition = {
          ...(updatedRule.condition || {}),
          [conditionField]: value
        };
      } else {
        updatedRule[field] = value;
      }

      currentRules[index] = updatedRule;

      return {
        ...prev,
        conditions: {
          ...(prev.conditions || {}),
          nextStageRules: currentRules
        }
      };
    });
  }, []);

  // Get available fields for condition building
  const availableFields = useMemo(() => {
    const fields = editedStage?.dataCollection?.fields || [];
    return fields.map(f => ({
      label: f.label || f.name,
      value: f.name,
      type: f.type
    }));
  }, [editedStage?.dataCollection?.fields]);

  // Available operators
  const availableOperators = [
    { label: 'Equals', value: 'equals', types: ['all'] },
    { label: 'Not Equals', value: 'not_equals', types: ['all'] },
    { label: 'Greater Than', value: 'greater_than', types: ['number', 'date'] },
    { label: 'Less Than', value: 'less_than', types: ['number', 'date'] },
    { label: 'Greater or Equal', value: 'greater_or_equal', types: ['number', 'date'] },
    { label: 'Less or Equal', value: 'less_or_equal', types: ['number', 'date'] },
    { label: 'Contains', value: 'contains', types: ['text', 'email', 'string'] },
    { label: 'Not Contains', value: 'not_contains', types: ['text', 'email', 'string'] },
    { label: 'Starts With', value: 'starts_with', types: ['text', 'email', 'string'] },
    { label: 'Ends With', value: 'ends_with', types: ['text', 'email', 'string'] },
    { label: 'Is Empty', value: 'is_empty', types: ['all'] },
    { label: 'Is Not Empty', value: 'is_not_empty', types: ['all'] }
  ];

  if (!isVisible || !editedStage) return null;

  const stageTypeInfo = getStageTypeInfo(editedStage.type);

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return (
          <View style={{ gap: 20 }}>
            {/* Title */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Stage Title
              </Text>
              <TextInput
                value={editedStage.title}
                onChangeText={(value) => updateField('title', value)}
                placeholder="Enter stage title"
                placeholderTextColor={editorColors.secondaryText}
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border
                }}
              />
            </View>

            {/* Description */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Description
              </Text>
              <TextInput
                value={editedStage.description}
                onChangeText={(value) => updateField('description', value)}
                placeholder="Enter stage description"
                placeholderTextColor={editorColors.secondaryText}
                multiline
                numberOfLines={4}
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border,
                  minHeight: 100,
                  textAlignVertical: 'top'
                }}
              />
            </View>

            {/* Type Selector */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Stage Type
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ gap: 8 }}>
                <XStack gap={8}>
                  {['greeting', 'introduction', 'data_collection', 'verification', 'setup', 'training', 'completion', 'custom'].map(type => {
                    const typeInfo = getStageTypeInfo(type);
                    const isSelected = editedStage.type === type;
                    return (
                      <Pressable
                        key={type}
                        onPress={() => updateField('type', type)}
                        style={{
                          paddingHorizontal: 16,
                          paddingVertical: 10,
                          borderRadius: 8,
                          backgroundColor: isSelected ? typeInfo.color : editorColors.cardBg,
                          borderWidth: 1,
                          borderColor: isSelected ? typeInfo.color : editorColors.border,
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: 6
                        }}
                      >
                        <MaterialCommunityIcons
                          name={typeInfo.icon as any}
                          size={16}
                          color={isSelected ? 'white' : editorColors.text}
                        />
                        <Text style={{
                          fontSize: 12,
                          fontWeight: '600',
                          color: isSelected ? 'white' : editorColors.text,
                          textTransform: 'capitalize'
                        }}>
                          {type.replace('_', ' ')}
                        </Text>
                      </Pressable>
                    );
                  })}
                </XStack>
              </ScrollView>
            </View>

            {/* Order */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Order in Flow
              </Text>
              <TextInput
                value={String(editedStage.order)}
                onChangeText={(value) => updateField('order', parseInt(value) || 0)}
                placeholder="0"
                placeholderTextColor={editorColors.secondaryText}
                keyboardType="number-pad"
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border
                }}
              />
            </View>
          </View>
        );

      case 'config':
        return (
          <View style={{ gap: 20 }}>
            {/* Estimated Duration */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Estimated Duration (minutes)
              </Text>
              <TextInput
                value={String(editedStage.config?.estimatedDuration || '')}
                onChangeText={(value) => updateNestedField('config', 'estimatedDuration', parseInt(value) || 0)}
                placeholder="5"
                placeholderTextColor={editorColors.secondaryText}
                keyboardType="number-pad"
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border
                }}
              />
            </View>

            {/* Toggles */}
            <View style={{ gap: 16 }}>
              {/* Required */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                    Required Stage
                  </Text>
                  <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginTop: 4 }}>
                    User must complete this stage
                  </Text>
                </View>
                <Switch
                  value={editedStage.config?.required}
                  onValueChange={(value) => updateNestedField('config', 'required', value)}
                  trackColor={{ false: '#767577', true: editorColors.accent }}
                  thumbColor={editedStage.config?.required ? '#f4f3f4' : '#f4f3f4'}
                />
              </View>

              {/* Skippable */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                    Skippable
                  </Text>
                  <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginTop: 4 }}>
                    User can skip this stage
                  </Text>
                </View>
                <Switch
                  value={editedStage.config?.skippable}
                  onValueChange={(value) => updateNestedField('config', 'skippable', value)}
                  trackColor={{ false: '#767577', true: editorColors.accent }}
                  thumbColor={editedStage.config?.skippable ? '#f4f3f4' : '#f4f3f4'}
                />
              </View>

              {/* Repeatable */}
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                    Repeatable
                  </Text>
                  <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginTop: 4 }}>
                    Stage can be repeated
                  </Text>
                </View>
                <Switch
                  value={editedStage.config?.repeatable}
                  onValueChange={(value) => updateNestedField('config', 'repeatable', value)}
                  trackColor={{ false: '#767577', true: editorColors.accent }}
                  thumbColor={editedStage.config?.repeatable ? '#f4f3f4' : '#f4f3f4'}
                />
              </View>
            </View>

            {/* Max Repetitions */}
            {editedStage.config?.repeatable && (
              <View style={{ gap: 8 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                  Maximum Repetitions
                </Text>
                <TextInput
                  value={String(editedStage.config?.maxRepetitions || 1)}
                  onChangeText={(value) => updateNestedField('config', 'maxRepetitions', parseInt(value) || 1)}
                  placeholder="1"
                  placeholderTextColor={editorColors.secondaryText}
                  keyboardType="number-pad"
                  style={{
                    backgroundColor: editorColors.cardBg,
                    borderRadius: 8,
                    padding: 12,
                    fontSize: 14,
                    color: editorColors.text,
                    borderWidth: 1,
                    borderColor: editorColors.border
                  }}
                />
              </View>
            )}
          </View>
        );

      case 'voice':
        return (
          <View style={{ gap: 20 }}>
            {/* Provide Context Toggle */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                  Provide Context
                </Text>
                <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginTop: 4 }}>
                  AI will provide extra context about this stage
                </Text>
              </View>
              <Switch
                value={editedStage.voiceConfig?.provideContext}
                onValueChange={(value) => updateNestedField('voiceConfig', 'provideContext', value)}
                trackColor={{ false: '#767577', true: editorColors.accent }}
                thumbColor={editedStage.voiceConfig?.provideContext ? '#f4f3f4' : '#f4f3f4'}
              />
            </View>

            {/* AI Prompt */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                AI Prompt
              </Text>
              <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginBottom: 4 }}>
                Custom instructions for AI during this stage
              </Text>
              <TextInput
                value={editedStage.voiceConfig?.aiPrompt}
                onChangeText={(value) => updateNestedField('voiceConfig', 'aiPrompt', value)}
                placeholder="Guide the user through this stage..."
                placeholderTextColor={editorColors.secondaryText}
                multiline
                numberOfLines={4}
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border,
                  minHeight: 100,
                  textAlignVertical: 'top'
                }}
              />
            </View>

            {/* Expected Phrases */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Expected Phrases
              </Text>
              <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginBottom: 4 }}>
                Example phrases users might say (comma-separated)
              </Text>
              <TextInput
                value={editedStage.voiceConfig?.expectedPhrases?.join(', ')}
                onChangeText={(value) => updateNestedField('voiceConfig', 'expectedPhrases', value.split(',').map(s => s.trim()))}
                placeholder="yes, continue, next step"
                placeholderTextColor={editorColors.secondaryText}
                multiline
                numberOfLines={3}
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border,
                  minHeight: 80,
                  textAlignVertical: 'top'
                }}
              />
            </View>

            {/* Navigation Keywords */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Navigation Keywords
              </Text>
              <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginBottom: 4 }}>
                Keywords for auto-navigation (comma-separated)
              </Text>
              <TextInput
                value={editedStage.voiceConfig?.navigationKeywords?.join(', ')}
                onChangeText={(value) => updateNestedField('voiceConfig', 'navigationKeywords', value.split(',').map(s => s.trim()))}
                placeholder="next, continue, proceed"
                placeholderTextColor={editorColors.secondaryText}
                multiline
                numberOfLines={2}
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border,
                  minHeight: 60,
                  textAlignVertical: 'top'
                }}
              />
            </View>

            {/* RAG Enhancement */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                RAG Enhancement Query
              </Text>
              <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginBottom: 4 }}>
                Query to enhance RAG responses for this stage
              </Text>
              <TextInput
                value={editedStage.voiceConfig?.ragEnhancement}
                onChangeText={(value) => updateNestedField('voiceConfig', 'ragEnhancement', value)}
                placeholder="product features, pricing information"
                placeholderTextColor={editorColors.secondaryText}
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border
                }}
              />
            </View>
          </View>
        );

      case 'data':
        return (
          <View style={{ gap: 20 }}>
            {/* User Data Schema Link */}
            <View style={{
              backgroundColor: editorColors.cardBg,
              borderRadius: 12,
              padding: 16,
              borderWidth: 1,
              borderColor: editorColors.border
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <MaterialCommunityIcons name="database-sync" size={20} color={editorColors.accent} />
                <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text }}>
                  Link to User Data Schema
                </Text>
              </View>
              <Text style={{ fontSize: 13, color: editorColors.secondaryText, lineHeight: 18, marginBottom: 12 }}>
                Link this stage to a user_datas collection to dynamically load schema fields for TTS.
              </Text>

              {editedStage.dataCollection?.form ? (
                <View style={{
                  backgroundColor: theme ? '#dcfce7' : '#14532d',
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 12
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <MaterialCommunityIcons name="check-circle" size={16} color={theme ? '#16a34a' : '#86efac'} />
                    <Text style={{ fontSize: 13, fontWeight: '600', color: theme ? '#16a34a' : '#86efac' }}>
                      Linked to Schema: {editedStage.dataCollection.form}
                    </Text>
                  </View>
                </View>
              ) : (
                <View style={{
                  backgroundColor: theme ? '#fef3c7' : '#78350f',
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 12
                }}>
                  <Text style={{ fontSize: 13, color: theme ? '#d97706' : '#fbbf24' }}>
                    No schema linked. Click below to select from user_datas.
                  </Text>
                </View>
              )}

              <Pressable
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  backgroundColor: editorColors.accent,
                  borderRadius: 8,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 8
                }}
                onPress={() => {
                  if (!flowId) {
                    Alert.alert('Error', 'Flow ID is required to select user data');
                    return;
                  }
                  setShowUserDataSelector(true);
                }}
              >
                <MaterialCommunityIcons name="link-variant" size={16} color="white" />
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>
                  {editedStage.dataCollection?.form ? 'Change Linked Schema' : 'Link to User Data'}
                </Text>
              </Pressable>
            </View>

            {/* Data Collection Fields */}
            <View style={{
              backgroundColor: editorColors.cardBg,
              borderRadius: 12,
              padding: 16,
              borderWidth: 1,
              borderColor: editorColors.border
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <MaterialCommunityIcons name="form-select" size={20} color={editorColors.accent} />
                <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text }}>
                  Data Collection Fields
                </Text>
              </View>
              <Text style={{ fontSize: 13, color: editorColors.secondaryText, lineHeight: 18 }}>
                Fields to collect: {editedStage.dataCollection?.fields?.length || 0}
              </Text>
              {editedStage.dataCollection?.fields && editedStage.dataCollection.fields.length > 0 && (
                <View style={{ marginTop: 12, gap: 8 }}>
                  {editedStage.dataCollection.fields.map((field, index) => (
                    <View
                      key={index}
                      style={{
                        backgroundColor: theme ? 'white' : '#1e293b',
                        padding: 12,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: editorColors.border
                      }}
                    >
                      {/* Field Header with Title and Type */}
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                        <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Text style={{ fontSize: 14, fontWeight: '700', color: editorColors.text }}>
                            {field.label || field.name}
                            {field.required && <Text style={{ color: '#ef4444' }}> *</Text>}
                          </Text>
                          {field.name !== field.label && (
                            <Text style={{ fontSize: 10, color: editorColors.secondaryText, fontStyle: 'italic' }}>
                              ({field.name})
                            </Text>
                          )}
                        </View>
                        <View style={{
                          backgroundColor: theme ? '#e0e7ff' : '#312e81',
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          borderRadius: 6
                        }}>
                          <Text style={{ fontSize: 10, fontWeight: '600', color: theme ? '#4f46e5' : '#a5b4fc' }}>
                            {field.type}
                          </Text>
                        </View>
                      </View>

                      {/* Field Metadata */}
                      <View style={{ gap: 4 }}>
                        {field.helpText && (
                          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 4 }}>
                            <MaterialCommunityIcons name="information" size={12} color={theme ? '#64748b' : '#94a3b8'} style={{ marginTop: 2 }} />
                            <Text style={{ fontSize: 11, color: editorColors.secondaryText, flex: 1, lineHeight: 16 }}>
                              {field.helpText}
                            </Text>
                          </View>
                        )}
                        {field.example && (
                          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 4 }}>
                            <MaterialCommunityIcons name="lightbulb-outline" size={12} color={theme ? '#f59e0b' : '#fbbf24'} style={{ marginTop: 2 }} />
                            <Text style={{ fontSize: 11, color: theme ? '#d97706' : '#fbbf24', flex: 1 }}>
                              Example: {field.example}
                            </Text>
                          </View>
                        )}
                        {field.validation && (
                          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 4 }}>
                            <MaterialCommunityIcons name="check-decagram" size={12} color={theme ? '#10b981' : '#34d399'} style={{ marginTop: 2 }} />
                            <Text style={{ fontSize: 10, color: theme ? '#059669' : '#34d399', flex: 1 }}>
                              {typeof field.validation === 'string'
                                ? `Format: ${field.validation}`
                                : typeof field.validation === 'object'
                                  ? `Rules: ${Object.entries(field.validation).map(([k, v]) => `${k}: ${v}`).join(', ')}`
                                  : `Validation: ${String(field.validation)}`
                              }
                            </Text>
                          </View>
                        )}
                        {field.options && field.options.length > 0 && (
                          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 4 }}>
                            <MaterialCommunityIcons name="format-list-bulleted" size={12} color={theme ? '#3b82f6' : '#60a5fa'} style={{ marginTop: 2 }} />
                            <Text style={{ fontSize: 10, color: theme ? '#3b82f6' : '#60a5fa', flex: 1 }}>
                              Options: {field.options.join(', ')}
                            </Text>
                          </View>
                        )}
                      </View>
                    </View>
                  ))}
                </View>
              )}
              <Pressable
                style={{
                  marginTop: 12,
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  backgroundColor: editorColors.accent,
                  borderRadius: 8,
                  alignItems: 'center'
                }}
                onPress={() => Alert.alert('Data Collection', 'Advanced data collection editor coming soon!')}
              >
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>
                  Configure Fields
                </Text>
              </Pressable>
            </View>
          </View>
        );

      case 'actions':
        return (
          <View style={{ gap: 20 }}>
            <View style={{
              backgroundColor: editorColors.cardBg,
              borderRadius: 12,
              padding: 16,
              borderWidth: 1,
              borderColor: editorColors.border
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <MaterialCommunityIcons name="lightning-bolt" size={20} color={editorColors.accent} />
                <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text }}>
                  Stage Actions
                </Text>
              </View>
              <Text style={{ fontSize: 13, color: editorColors.secondaryText, lineHeight: 18 }}>
                Automated actions: {editedStage.stageActions?.length || 0}
              </Text>
              {editedStage.stageActions && editedStage.stageActions.length > 0 && (
                <View style={{ marginTop: 12, gap: 8 }}>
                  {editedStage.stageActions.map((action, index) => (
                    <View
                      key={index}
                      style={{
                        backgroundColor: theme ? 'white' : '#1e293b',
                        padding: 10,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: editorColors.border
                      }}
                    >
                      <Text style={{ fontSize: 13, fontWeight: '600', color: editorColors.text }}>
                        {action.actionType.replace('_', ' ').toUpperCase()}
                      </Text>
                      <Text style={{ fontSize: 11, color: editorColors.secondaryText, marginTop: 2 }}>
                        Trigger: {action.triggerOn}
                      </Text>
                    </View>
                  ))}
                </View>
              )}
              <XStack gap={8} marginTop={12} flexWrap="wrap">
                <Pressable
                  style={{
                    flex: 1,
                    minWidth: 120,
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    backgroundColor: theme ? '#dbeafe' : '#1e40af',
                    borderRadius: 8,
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    gap: 6
                  }}
                  onPress={() => {
                    if (!flowId) {
                      Alert.alert('Error', 'Flow ID is required to select calendar');
                      return;
                    }
                    const newAction = {
                      actionType: 'schedule_appointment',
                      triggerOn: 'enter',
                      calendarConfig: {
                        calendarId: '',
                        duration: 30,
                        buffer: 15,
                        autoConfirm: false
                      }
                    };
                    const currentActions = editedStage.stageActions || [];
                    updateField('stageActions', [...currentActions, newAction]);
                    // Open calendar selector immediately
                    setShowCalendarSelector(true);
                  }}
                >
                  <MaterialCommunityIcons name="calendar-plus" size={14} color={theme ? '#1e40af' : '#93c5fd'} />
                  <Text style={{ color: theme ? '#1e40af' : '#93c5fd', fontWeight: '600', fontSize: 12 }}>
                    Schedule Appointment
                  </Text>
                </Pressable>

                <Pressable
                  style={{
                    flex: 1,
                    minWidth: 120,
                    paddingVertical: 10,
                    paddingHorizontal: 16,
                    backgroundColor: editorColors.accent,
                    borderRadius: 8,
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    gap: 6
                  }}
                  onPress={() => Alert.alert('Stage Actions', 'Other action types coming soon!')}
                >
                  <MaterialIcons name="add" size={14} color="white" />
                  <Text style={{ color: 'white', fontWeight: '600', fontSize: 12 }}>
                    Other Actions
                  </Text>
                </Pressable>
              </XStack>

              {/* Action Types Reference */}
              <View style={{ marginTop: 16, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                  Available Action Types:
                </Text>
                <Text style={{ fontSize: 11, color: editorColors.secondaryText, lineHeight: 16 }}>
                  schedule_appointment, send_email, send_sms, create_task, update_record, call_webhook, custom
                </Text>
              </View>
            </View>
          </View>
        );

      case 'conditions':
        return (
          <View style={{ gap: 20 }}>
            {/* Conditional Branching (IFTTT) */}
            <View style={{
              backgroundColor: editorColors.cardBg,
              borderRadius: 12,
              padding: 16,
              borderWidth: 1,
              borderColor: editorColors.border
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <MaterialCommunityIcons name="call-split" size={20} color={editorColors.accent} />
                <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text }}>
                  Conditional Routing (IFTTT)
                </Text>
              </View>
              <Text style={{ fontSize: 13, color: editorColors.secondaryText, lineHeight: 18, marginBottom: 16 }}>
                Define IF-THEN rules to route users to different stages based on collected data.
              </Text>

              {/* Available Fields Info */}
              {availableFields.length > 0 ? (
                <View style={{
                  padding: 12,
                  backgroundColor: theme ? '#dcfce7' : '#14532d',
                  borderRadius: 8,
                  marginBottom: 16
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <MaterialCommunityIcons name="check-circle" size={14} color={theme ? '#16a34a' : '#86efac'} />
                    <Text style={{ fontSize: 12, fontWeight: '600', color: theme ? '#16a34a' : '#86efac' }}>
                      {availableFields.length} fields available for conditions
                    </Text>
                  </View>
                  <Text style={{ fontSize: 11, color: theme ? '#15803d' : '#4ade80' }}>
                    {availableFields.map(f => f.label).join(', ')}
                  </Text>
                </View>
              ) : (
                <View style={{
                  padding: 12,
                  backgroundColor: theme ? '#fef3c7' : '#78350f',
                  borderRadius: 8,
                  marginBottom: 16
                }}>
                  <Text style={{ fontSize: 12, color: theme ? '#d97706' : '#fbbf24' }}>
                    ⚠️ No data collection fields defined. Add fields in the Data tab first, or conditions won't have data to evaluate.
                  </Text>
                </View>
              )}

              {/* Existing Conditions with Interactive Editors */}
              {editedStage.conditions?.nextStageRules && editedStage.conditions.nextStageRules.length > 0 ? (
                <View style={{ gap: 12, marginBottom: 16 }}>
                  {editedStage.conditions.nextStageRules.map((rule, index) => (
                    <View
                      key={index}
                      style={{
                        backgroundColor: theme ? 'white' : '#1e293b',
                        padding: 12,
                        borderRadius: 8,
                        borderWidth: 1,
                        borderColor: editorColors.border
                      }}
                    >
                      {/* Condition Header */}
                      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <View style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          backgroundColor: theme ? '#f3e8ff' : '#4c1d95',
                          paddingHorizontal: 8,
                          paddingVertical: 4,
                          borderRadius: 6
                        }}>
                          <MaterialCommunityIcons name="filter" size={12} color={theme ? '#7c3aed' : '#c4b5fd'} />
                          <Text style={{ fontSize: 11, fontWeight: '600', color: theme ? '#7c3aed' : '#c4b5fd', marginLeft: 4 }}>
                            Rule {index + 1}
                          </Text>
                        </View>
                        <Pressable
                          onPress={() => {
                            Alert.alert(
                              'Delete Condition',
                              'Are you sure you want to delete this condition rule?',
                              [
                                { text: 'Cancel', style: 'cancel' },
                                {
                                  text: 'Delete',
                                  style: 'destructive',
                                  onPress: () => {
                                    const newRules = [...(editedStage.conditions?.nextStageRules || [])];
                                    newRules.splice(index, 1);
                                    updateNestedField('conditions', 'nextStageRules', newRules);
                                  }
                                }
                              ]
                            );
                          }}
                          style={{ padding: 4 }}
                        >
                          <MaterialIcons name="delete" size={18} color="#ef4444" />
                        </Pressable>
                      </View>

                      {/* Field Selector */}
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                          IF field:
                        </Text>
                        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                          <XStack gap={6}>
                            {availableFields.length > 0 ? availableFields.map((field) => (
                              <Pressable
                                key={field.value}
                                onPress={() => updateConditionRule(index, 'condition.field', field.value)}
                                style={{
                                  paddingHorizontal: 12,
                                  paddingVertical: 6,
                                  borderRadius: 6,
                                  backgroundColor: rule.condition?.field === field.value
                                    ? editorColors.accent
                                    : (theme ? '#f1f5f9' : '#334155'),
                                  borderWidth: 1,
                                  borderColor: rule.condition?.field === field.value
                                    ? editorColors.accent
                                    : editorColors.border
                                }}
                              >
                                <Text style={{
                                  fontSize: 11,
                                  fontWeight: '600',
                                  color: rule.condition?.field === field.value
                                    ? 'white'
                                    : editorColors.text
                                }}>
                                  {field.label}
                                </Text>
                              </Pressable>
                            )) : (
                              <Text style={{ fontSize: 11, color: editorColors.secondaryText, fontStyle: 'italic' }}>
                                Add fields in Data tab first
                              </Text>
                            )}
                          </XStack>
                        </ScrollView>
                      </View>

                      {/* Operator Selector */}
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                          Operator:
                        </Text>
                        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                          <XStack gap={6}>
                            {availableOperators.map((op) => (
                              <Pressable
                                key={op.value}
                                onPress={() => updateConditionRule(index, 'condition.operator', op.value)}
                                style={{
                                  paddingHorizontal: 10,
                                  paddingVertical: 6,
                                  borderRadius: 6,
                                  backgroundColor: rule.condition?.operator === op.value
                                    ? editorColors.accent
                                    : (theme ? '#f1f5f9' : '#334155'),
                                  borderWidth: 1,
                                  borderColor: rule.condition?.operator === op.value
                                    ? editorColors.accent
                                    : editorColors.border
                                }}
                              >
                                <Text style={{
                                  fontSize: 10,
                                  fontWeight: '600',
                                  color: rule.condition?.operator === op.value
                                    ? 'white'
                                    : editorColors.text
                                }}>
                                  {op.label}
                                </Text>
                              </Pressable>
                            ))}
                          </XStack>
                        </ScrollView>
                      </View>

                      {/* Value Input */}
                      <View style={{ marginBottom: 12 }}>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                          Value:
                        </Text>
                        <TextInput
                          value={String(rule.condition?.value || '')}
                          onChangeText={(value) => updateConditionRule(index, 'condition.value', value)}
                          placeholder="Enter value to compare"
                          placeholderTextColor={editorColors.secondaryText}
                          style={{
                            backgroundColor: theme ? 'white' : '#0f172a',
                            borderRadius: 8,
                            padding: 10,
                            fontSize: 13,
                            color: editorColors.text,
                            borderWidth: 1,
                            borderColor: editorColors.border
                          }}
                        />
                      </View>

                      {/* Next Stage Selector */}
                      <View>
                        <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                          THEN go to stage:
                        </Text>
                        <TextInput
                          value={String(rule.nextStageIndex !== undefined ? rule.nextStageIndex : '')}
                          onChangeText={(value) => updateConditionRule(index, 'nextStageIndex', parseInt(value) || 0)}
                          placeholder="Enter stage index (0, 1, 2...)"
                          placeholderTextColor={editorColors.secondaryText}
                          keyboardType="number-pad"
                          style={{
                            backgroundColor: theme ? 'white' : '#0f172a',
                            borderRadius: 8,
                            padding: 10,
                            fontSize: 13,
                            color: editorColors.text,
                            borderWidth: 1,
                            borderColor: editorColors.border
                          }}
                        />
                        <Text style={{ fontSize: 10, color: editorColors.secondaryText, marginTop: 4 }}>
                          💡 Stage 0 is first, Stage 1 is second, etc.
                        </Text>
                      </View>

                      {/* Preview */}
                      <View style={{
                        marginTop: 12,
                        padding: 10,
                        backgroundColor: theme ? '#f3e8ff' : '#4c1d95',
                        borderRadius: 6,
                        borderLeftWidth: 3,
                        borderLeftColor: editorColors.accent
                      }}>
                        <Text style={{ fontSize: 11, fontWeight: '600', color: theme ? '#6d28d9' : '#c4b5fd' }}>
                          Preview: IF {rule.condition?.field || '[field]'} {rule.condition?.operator || '[operator]'} "{rule.condition?.value || '[value]'}" THEN go to stage {rule.nextStageIndex !== undefined ? rule.nextStageIndex : '[index]'}
                        </Text>
                      </View>
                    </View>
                  ))}
                </View>
              ) : (
                <View style={{
                  padding: 16,
                  backgroundColor: theme ? '#f1f5f9' : '#334155',
                  borderRadius: 8,
                  marginBottom: 16
                }}>
                  <Text style={{
                    fontSize: 13,
                    color: editorColors.secondaryText,
                    textAlign: 'center'
                  }}>
                    No conditions defined. Click "Add Condition" to create conditional routing.
                  </Text>
                </View>
              )}

              <Pressable
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  backgroundColor: editorColors.accent,
                  borderRadius: 8,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 8
                }}
                onPress={() => {
                  // Check if fields are available
                  if (availableFields.length === 0) {
                    Alert.alert(
                      'No Fields Available',
                      'Add data collection fields in the Data tab first. Conditions need fields to evaluate.',
                      [{ text: 'OK' }]
                    );
                    return;
                  }

                  // Initialize conditions if not present
                  const currentConditions = editedStage.conditions || {};
                  const currentRules = currentConditions.nextStageRules || [];

                  // Add new condition rule template with first available field
                  const newRule = {
                    condition: {
                      field: availableFields[0]?.value || '',
                      operator: 'equals',
                      value: ''
                    },
                    nextStageIndex: 0
                  };

                  updateNestedField('conditions', 'nextStageRules', [...currentRules, newRule]);
                }}
              >
                <MaterialIcons name="add" size={16} color="white" />
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>
                  Add Condition Rule
                </Text>
              </Pressable>

              {/* Quick Guide */}
              <View style={{ marginTop: 16, padding: 12, backgroundColor: theme ? '#eff6ff' : '#1e3a8a', borderRadius: 8 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: theme ? '#1e40af' : '#93c5fd', marginBottom: 6 }}>
                  💡 How Conditional Routing Works:
                </Text>
                <Text style={{ fontSize: 11, color: theme ? '#3b82f6' : '#93c5fd', lineHeight: 16 }}>
                  1. User completes this stage and provides data{'\n'}
                  2. System evaluates conditions in order (top to bottom){'\n'}
                  3. First matching condition determines next stage{'\n'}
                  4. If no conditions match, proceeds to default next stage
                </Text>
              </View>

              {/* Operators Reference */}
              <View style={{ marginTop: 12, padding: 12, backgroundColor: theme ? '#f8fafc' : '#1e293b', borderRadius: 8 }}>
                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 8 }}>
                  Available Operators:
                </Text>
                <View style={{ gap: 4 }}>
                  <Text style={{ fontSize: 10, color: editorColors.secondaryText }}>
                    • Equality: equals, not_equals
                  </Text>
                  <Text style={{ fontSize: 10, color: editorColors.secondaryText }}>
                    • Comparison: greater_than, less_than, greater_or_equal, less_or_equal
                  </Text>
                  <Text style={{ fontSize: 10, color: editorColors.secondaryText }}>
                    • Text: contains, not_contains, starts_with, ends_with
                  </Text>
                  <Text style={{ fontSize: 10, color: editorColors.secondaryText }}>
                    • Null checks: is_empty, is_not_empty
                  </Text>
                </View>
              </View>
            </View>
          </View>
        );

      case 'verification':
        return (
          <View style={{ gap: 20 }}>
            {/* Verification Data Schema Link */}
            <View style={{
              backgroundColor: editorColors.cardBg,
              borderRadius: 12,
              padding: 16,
              borderWidth: 1,
              borderColor: editorColors.border
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <MaterialCommunityIcons name="shield-check" size={20} color={editorColors.accent} />
                <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text }}>
                  Link to Verification Database
                </Text>
              </View>
              <Text style={{ fontSize: 13, color: editorColors.secondaryText, lineHeight: 18, marginBottom: 12 }}>
                Select a data schema containing user records to verify against.
              </Text>

              {editedStage.verificationConfig?.dataSchemaId ? (
                <View style={{
                  backgroundColor: theme ? '#dcfce7' : '#14532d',
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 12
                }}>
                  <Text style={{ fontSize: 13, fontWeight: '600', color: theme ? '#16a34a' : '#86efac' }}>
                    ✓ Linked to: {editedStage.verificationConfig.dataSchemaId}
                  </Text>
                </View>
              ) : (
                <View style={{
                  backgroundColor: theme ? '#fef3c7' : '#78350f',
                  padding: 12,
                  borderRadius: 8,
                  marginBottom: 12
                }}>
                  <Text style={{ fontSize: 13, color: theme ? '#d97706' : '#fbbf24' }}>
                    No schema linked. Click below to select.
                  </Text>
                </View>
              )}

              <Pressable
                style={{
                  paddingVertical: 10,
                  paddingHorizontal: 16,
                  backgroundColor: editorColors.accent,
                  borderRadius: 8,
                  alignItems: 'center',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  gap: 8
                }}
                onPress={() => setShowVerificationDataSelector(true)}
              >
                <MaterialCommunityIcons name="link-variant" size={16} color="white" />
                <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>
                  {editedStage.verificationConfig?.dataSchemaId ? 'Change Schema' : 'Link Schema'}
                </Text>
              </Pressable>
            </View>

            {/* Fields to Verify Selection */}
            {editedStage.verificationConfig?.availableFields && editedStage.verificationConfig.availableFields.length > 0 && (
              <View style={{
                backgroundColor: editorColors.cardBg,
                borderRadius: 12,
                padding: 16,
                borderWidth: 1,
                borderColor: editorColors.border
              }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <MaterialCommunityIcons name="checkbox-marked-circle" size={20} color={editorColors.accent} />
                  <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text }}>
                    Select Fields to Verify
                  </Text>
                </View>
                <Text style={{ fontSize: 13, color: editorColors.secondaryText, lineHeight: 18, marginBottom: 12 }}>
                  Choose which fields to use for user identification.
                </Text>

                <View style={{ gap: 8 }}>
                  {editedStage.verificationConfig.availableFields.map((field, index) => {
                    const isSelected = editedStage.verificationConfig?.fieldsToVerify?.includes(field.name);
                    return (
                      <Pressable
                        key={index}
                        onPress={() => {
                          const currentFields = editedStage.verificationConfig?.fieldsToVerify || [];
                          const newFields = isSelected
                            ? currentFields.filter(f => f !== field.name)
                            : [...currentFields, field.name];

                          setEditedStage(prev => {
                            if (!prev) return null;
                            return {
                              ...prev,
                              verificationConfig: {
                                ...prev.verificationConfig,
                                fieldsToVerify: newFields
                              }
                            };
                          });
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          borderRadius: 8,
                          backgroundColor: isSelected
                            ? (theme ? '#dbeafe' : '#1e40af')
                            : (theme ? 'white' : '#1e293b'),
                          borderWidth: 1,
                          borderColor: isSelected
                            ? editorColors.accent
                            : editorColors.border
                        }}
                      >
                        <MaterialCommunityIcons
                          name={isSelected ? 'checkbox-marked' : 'checkbox-blank-outline'}
                          size={20}
                          color={isSelected ? editorColors.accent : editorColors.secondaryText}
                        />
                        <Text style={{
                          marginLeft: 10,
                          fontSize: 14,
                          fontWeight: isSelected ? '600' : '400',
                          color: editorColors.text,
                          flex: 1
                        }}>
                          {field.label}
                        </Text>
                        <View style={{
                          backgroundColor: theme ? '#e0e7ff' : '#312e81',
                          paddingHorizontal: 8,
                          paddingVertical: 3,
                          borderRadius: 6
                        }}>
                          <Text style={{ fontSize: 10, fontWeight: '600', color: theme ? '#4f46e5' : '#a5b4fc' }}>
                            {field.type}
                          </Text>
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              </View>
            )}

            {/* Match Threshold */}
            <View style={{ gap: 8 }}>
              <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                Match Threshold (%)
              </Text>
              <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginBottom: 4 }}>
                Percentage of fields that must match for successful verification
              </Text>
              <TextInput
                value={String(editedStage.verificationConfig?.matchThreshold || 100)}
                onChangeText={(value) => {
                  const threshold = parseInt(value) || 100;
                  setEditedStage(prev => {
                    if (!prev) return null;
                    return {
                      ...prev,
                      verificationConfig: {
                        ...prev.verificationConfig,
                        matchThreshold: Math.min(100, Math.max(0, threshold))
                      }
                    };
                  });
                }}
                placeholder="100"
                placeholderTextColor={editorColors.secondaryText}
                keyboardType="number-pad"
                style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 14,
                  color: editorColors.text,
                  borderWidth: 1,
                  borderColor: editorColors.border
                }}
              />
            </View>
          </View>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Backdrop */}
      <MotiView
        from={{ opacity: 0 }}
        animate={{ opacity: isVisible ? 0.5 : 0 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'black',
          zIndex: 1000,
        }}
        onTouchEnd={onClose}
      />

      {/* Editor Panel */}
      <MotiView
        from={{
          translateX: width,
          opacity: 0
        }}
        animate={{
          translateX: isVisible ? 0 : width,
          opacity: isVisible ? 1 : 0
        }}
        exit={{
          translateX: width,
          opacity: 0
        }}
        transition={{
          type: 'timing',
          duration: 300,
        }}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          width: Platform.OS === 'web' ? width * 0.4 : width * 0.85,
          height: '100%',
          backgroundColor: editorColors.background,
          zIndex: 1001,
          ...Platform.select({
            web: {
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)',
            },
            default: {
              shadowColor: '#000',
              shadowOffset: { width: 2, height: 0 },
              shadowOpacity: 0.3,
              shadowRadius: 10,
              elevation: 10,
            }
          })
        }}
      >
        {/* Header */}
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: 16,
          paddingVertical: 16,
          backgroundColor: editorColors.surface,
          borderBottomWidth: 1,
          borderBottomColor: editorColors.border,
        }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
            <MaterialCommunityIcons
              name={stageTypeInfo.icon as any}
              size={24}
              color={stageTypeInfo.color}
              style={{ marginRight: 12 }}
            />
            <View style={{ flex: 1 }}>
              <Text style={{
                fontSize: 16,
                fontWeight: '600',
                color: editorColors.text,
              }} numberOfLines={1}>
                {editedStage.title}
              </Text>
              <Text style={{
                fontSize: 12,
                color: editorColors.secondaryText,
              }}>
                {editedStage.type.toUpperCase().replace('_', ' ')} • Stage #{editedStage.order + 1}
              </Text>
            </View>
          </View>

          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <Pressable
              onPress={handleSave}
              style={{
                paddingHorizontal: 16,
                paddingVertical: 8,
                borderRadius: 8,
                backgroundColor: editorColors.accent,
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6
              }}
            >
              <MaterialIcons name="save" size={18} color="white" />
              <Text style={{ color: 'white', fontWeight: '600', fontSize: 13 }}>Save</Text>
            </Pressable>

            <Pressable
              onPress={onClose}
              style={{
                padding: 8,
                borderRadius: 8,
                backgroundColor: editorColors.border,
              }}
            >
              <MaterialCommunityIcons
                name="close"
                size={20}
                color={editorColors.text}
              />
            </Pressable>
          </View>
        </View>

        {/* Tabs */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{
            borderBottomWidth: 1,
            maxHeight: 60,
            borderBottomColor: editorColors.border,
            backgroundColor: editorColors.surface
          }}
          contentContainerStyle={{ paddingHorizontal: 16 }}
        >
          <XStack gap={8} maxHeight={60} paddingVertical={12}>
            {[
              { id: 'basic', label: 'Basic', icon: 'information' },
              { id: 'config', label: 'Config', icon: 'cog' },
              { id: 'voice', label: 'Voice', icon: 'microphone' },
              { id: 'data', label: 'Data', icon: 'form-select' },
              { id: 'actions', label: 'Actions', icon: 'lightning-bolt' },
              { id: 'conditions', label: 'Conditions', icon: 'call-split' },
              { id: 'verification', label: 'Verification', icon: 'shield-check' },
            ].map(tab => (
              <Pressable
                key={tab.id}
                onPress={() => setActiveTab(tab.id as any)}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 8,
                  backgroundColor: activeTab === tab.id ? editorColors.accent : 'transparent',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <MaterialCommunityIcons
                  name={tab.icon as any}
                  size={16}
                  color={activeTab === tab.id ? 'white' : editorColors.text}
                />
                <Text style={{
                  fontSize: 13,
                  fontWeight: '600',
                  color: activeTab === tab.id ? 'white' : editorColors.text
                }}>
                  {tab.label}
                </Text>
              </Pressable>
            ))}
          </XStack>
        </ScrollView>

        {/* Content */}
        <ScrollView
          style={{ flex: 1 }}
          contentContainerStyle={{ padding: 20 }}
          showsVerticalScrollIndicator={false}
        >
          {renderTabContent()}
        </ScrollView>
      </MotiView>

      {/* User Data Selector Modal */}
      {flowId && (
        <UserDataSelector
          isVisible={showUserDataSelector}
          onClose={() => setShowUserDataSelector(false)}
          onSelect={handleUserDataSelect}
          flowId={flowId}
        />
      )}

      {/* Calendar Selector Modal */}
      {flowId && (
        <CalendarSelector
          isVisible={showCalendarSelector}
          onClose={() => setShowCalendarSelector(false)}
          onSelect={handleCalendarSelect}
          flowId={flowId}
        />
      )}

      {/* Verification Data Selector Modal */}
      {flowId && (
        <UserDataSelector
          isVisible={showVerificationDataSelector}
          onClose={() => setShowVerificationDataSelector(false)}
          onSelect={handleVerificationDataSelect}
          flowId={flowId}
        />
      )}
    </>
  );
};

export default StageEditor;

